import { MouseEvent, useCallback } from 'react';
import { CheckboxCheckedIcon } from '@ardoq/icons';
import { CheckedWrapper, ReferenceCellElement } from '../atoms';
import {
  APIConstraintEnforcementMode,
  ArdoqId,
  LinkedComponent,
} from '@ardoq/api-types';
import { dispatchAction } from '@ardoq/rxbeach';
import { ComponentItemData, DependencyMatrixViewSettings } from '../types';
import { dependencyMatrixCellClicked } from '../viewModel$/dependencyMatrixLastClickedCell$';
import { REFERENCE_ID_ATTRIBUTE } from '@ardoq/global-consts';
import { INTEGRATION_CLASS } from 'contextMenus/consts';
import {
  IntendedRefTypes,
  isRefTypeBlocked,
  getBlockedRefTypeMessage,
} from 'utils/metamodelConstraints';
import {
  createReferenceFromCell,
  handleReferenceDeletion,
} from '../viewModel$/referenceCellInteractions';

type ReferenceCellProps = {
  viewSettings: DependencyMatrixViewSettings;
  references: LinkedComponent[];
  sourceId: ArdoqId;
  targetId: ArdoqId;
  sourceComponent: ComponentItemData;
  targetComponent: ComponentItemData;
  referenceCssClassNamesById: Record<ArdoqId, string>;
  selectedReferenceTypeName: string | null;
  workspaceId: ArdoqId;
  readOnly: boolean;
  highlightAsLastClicked: boolean;
  constraintEnforcementMode: APIConstraintEnforcementMode;
  intendedRefTypes: IntendedRefTypes | null;
};

const ReferenceCell = (props: ReferenceCellProps) => {
  const {
    viewSettings,
    references,
    targetId,
    sourceId,
    sourceComponent,
    targetComponent,
    referenceCssClassNamesById,
    selectedReferenceTypeName,
    readOnly,
    workspaceId,
    highlightAsLastClicked = false,
    constraintEnforcementMode,
    intendedRefTypes,
  } = props;

  const isValidSourceAndTargetComponent = Boolean(
    sourceComponent.isComponent && targetComponent.isComponent
  );

  const sourceTypeName = sourceComponent.typeName;
  const targetTypeName = targetComponent.typeName;
  const sourceWorkspaceId = sourceComponent.workspaceId;
  const targetWorkspaceId = targetComponent.workspaceId;

  const isBlockedByConstraints =
    isValidSourceAndTargetComponent &&
    !readOnly &&
    isRefTypeBlocked(
      intendedRefTypes,
      sourceTypeName,
      targetTypeName,
      selectedReferenceTypeName,
      constraintEnforcementMode
    );

  const blockedTooltipMessage = isBlockedByConstraints
    ? getBlockedRefTypeMessage(
        selectedReferenceTypeName,
        sourceTypeName,
        targetTypeName
      )
    : undefined;

  const referenceCellClick = useCallback(() => {
    if (isBlockedByConstraints) return;
    // toggle cell highlight
    dispatchAction(
      dependencyMatrixCellClicked({
        sourceId,
        targetId,
        highlightAsLastClicked: !highlightAsLastClicked,
      })
    );
    if (!isValidSourceAndTargetComponent || readOnly) {
      return;
    }

    createReferenceFromCell({
      sourceId,
      targetId,
      sourceWorkspaceId,
      targetWorkspaceId,
      currentReferenceCreationType:
        viewSettings.referenceCreationType[workspaceId],
      constraintEnforcementMode,
    });
  }, [
    isValidSourceAndTargetComponent,
    isBlockedByConstraints,
    readOnly,
    sourceId,
    targetId,
    sourceWorkspaceId,
    targetWorkspaceId,
    viewSettings.referenceCreationType,
    highlightAsLastClicked,
    workspaceId,
    constraintEnforcementMode,
  ]);

  const referenceClick = useCallback(
    async (event: MouseEvent, id: ArdoqId) => {
      event.stopPropagation();
      // toggle cell highlight
      dispatchAction(
        dependencyMatrixCellClicked({
          sourceId,
          targetId,
          highlightAsLastClicked: !highlightAsLastClicked,
        })
      );
      await handleReferenceDeletion({ id, readOnly });
    },
    [readOnly, sourceId, targetId, highlightAsLastClicked]
  );

  return (
    <ReferenceCellElement
      $isValidReference={isValidSourceAndTargetComponent}
      $isBlockedByConstraints={isBlockedByConstraints}
      onClick={referenceCellClick}
      $highlightAsLastClicked={highlightAsLastClicked}
      data-tooltip-text={blockedTooltipMessage}
    >
      {references.map(({ referenceId }) => (
        <CheckedWrapper
          key={referenceId}
          $readOnly={readOnly}
          {...{ [REFERENCE_ID_ATTRIBUTE]: referenceId }}
          onClick={event => referenceClick(event, referenceId)}
          className={INTEGRATION_CLASS}
        >
          <CheckboxCheckedIcon
            className={referenceCssClassNamesById[referenceId] || ''}
            style={{ strokeDasharray: 'none' }}
          />
        </CheckedWrapper>
      ))}
    </ReferenceCellElement>
  );
};

export { ReferenceCell };
